import React, { useEffect, useMemo } from 'react';
import {
  Box,
  Controller,
  ControllerWrapper,
  costFormater,
  DateInput,
  FieldWrapper,
  Label,
  PriceInput,
  SelectDropdown,
  Text,
  useFormContext,
  useWatch,
} from '@common-fe/common-fe';
import _ from 'lodash';
import styled from 'styled-components';

import {
  EnrollmentFormField,
  EnrollmentFormFields,
} from '@/modules/employee/Employee/EnrollmentModal/hooks/useEnrollmentForm';

import {
  ACTIVE_STATUS_KEY,
  CANCELLED_STATUS_KEY,
  COVERAGE_LEVEL_FAMILY_KEY,
  COVERAGE_LEVEL_FAMILY_LABEL,
  COVERAGE_LEVEL_SINGLE_KEY,
  COVERAGE_LEVEL_SINGLE_LABEL,
  LimitedUses,
} from '../enrollmentModal.types';
import { CurrentPlanInterface } from '../hooks/useGetEnrollmentSetupData';

const LimitedUseOptions = [
  { key: LimitedUses.LIMITED_USE, title: LimitedUses.LIMITED_USE, value: LimitedUses.LIMITED_USE },
  {
    key: LimitedUses.GENERALE_PURPOSE,
    title: LimitedUses.GENERALE_PURPOSE,
    value: LimitedUses.GENERALE_PURPOSE,
  },
  {
    key: LimitedUses.GENERALE_PURPOSE_DEDUCTIBLE_MET,
    title: LimitedUses.GENERALE_PURPOSE_DEDUCTIBLE_MET,
    value: LimitedUses.GENERALE_PURPOSE_DEDUCTIBLE_MET,
  },
];

const EnrollmentStatusOptions = [
  { key: ACTIVE_STATUS_KEY, title: 'Active', value: 'Active' },
  { key: CANCELLED_STATUS_KEY, title: 'Cancel', value: 'Cancel' },
];

export const PriceControl = styled((props) => <PriceInput {...props} />)`
  width: 160px;
  max-height: 40px;
  width: 320px;
`;

interface PlanFieldsProps {
  isEdit?: boolean;
  minElectionAmount?: number;
  maxElectionAmount?: number;
  employeeAge?: number;
  currentPlan?: CurrentPlanInterface;
  onChangeDateChange?: () => void;
}

const PlanFields: React.FC<PlanFieldsProps> = ({
  isEdit,
  minElectionAmount,
  maxElectionAmount,
  employeeAge,
  currentPlan,
  onChangeDateChange,
}) => {
  const {
    control,
    formState: { errors },
    clearErrors,
  } = useFormContext<EnrollmentFormFields>();
  const planYear = useWatch({ control, name: EnrollmentFormField.planYear });
  const limitedUse = useWatch({ control, name: EnrollmentFormField.limitedUse });
  const coverageLevel = useWatch({ control, name: EnrollmentFormField.coverageLevel });

  useEffect(() => {
    if (!coverageLevel && errors.coverageLevelEffectiveDate) {
      clearErrors(EnrollmentFormField.coverageLevelEffectiveDate);
    }
  }, [coverageLevel, errors.coverageLevelEffectiveDate, clearErrors]);

  const minAmount = useMemo(
    () => costFormater(minElectionAmount || 0, { isPrecision: true }),
    [minElectionAmount]
  );
  const maxAmount = useMemo(() => {
    if (currentPlan?.catchUpAge && employeeAge && employeeAge >= currentPlan?.catchUpAge) {
      return costFormater(_.toNumber(maxElectionAmount) + _.toNumber(currentPlan?.catchUpElection) || 0, {
        isPrecision: true,
      });
    }
    return costFormater(maxElectionAmount || 0, { isPrecision: true });
  }, [currentPlan?.catchUpAge, currentPlan?.catchUpElection, employeeAge, maxElectionAmount]);

  return (
    <Box gap="spacing16" data-testid="plan-fields-root">
      <FieldWrapper style={{ justifyContent: 'space-between' }} data-testid="plan-year-wrapper">
        <Label name={EnrollmentFormField.planYear} isNarrow data-testid="plan-year-label">
          Plan Year
        </Label>
        <ControllerWrapper style={{ flex: 'none' }} data-testid="plan-year-controller">
          <Controller
            name={EnrollmentFormField.planYear}
            control={control}
            render={({ field, fieldState }) => (
              <SelectDropdown
                id="planYear"
                name={EnrollmentFormField.planYear}
                errorText={fieldState.error?.message}
                options={
                  planYear
                    ? [
                        {
                          key: field?.value,
                          value: field?.value,
                          title: field?.value,
                        },
                      ]
                    : []
                }
                singleMode
                values={field?.value ? [field.value] : ['']}
                placeholder="Select plan year"
                inputStyle={{ minWidth: '320px', maxWidth: '320px' }}
                disabled
                data-testid="plan-year-select"
              />
            )}
          />
        </ControllerWrapper>
      </FieldWrapper>
      <FieldWrapper style={{ justifyContent: 'space-between' }} data-testid="plan-start-date-wrapper">
        <Label name={EnrollmentFormField.planStartDate} isNarrow data-testid="plan-start-date-label">
          Plan Start Date
        </Label>
        <ControllerWrapper style={{ flex: 'none' }} data-testid="plan-start-date-controller">
          <Controller
            name={EnrollmentFormField.planStartDate}
            control={control}
            render={({ field, fieldState }) => (
              <Box width="320px">
                <DateInput
                  isPicker
                  errorText={fieldState.error?.message}
                  isWide
                  testId="plan_start_date"
                  placeholder="Plan start date"
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  disabled
                  data-testid="plan-start-date-input"
                />
              </Box>
            )}
          />
        </ControllerWrapper>
      </FieldWrapper>
      <FieldWrapper style={{ justifyContent: 'space-between' }} data-testid="plan-end-date-wrapper">
        <Label name={EnrollmentFormField.planEndDate} isNarrow data-testid="plan-end-date-label">
          Plan End Date
        </Label>
        <ControllerWrapper style={{ flex: 'none' }} data-testid="plan-end-date-controller">
          <Controller
            name={EnrollmentFormField.planEndDate}
            control={control}
            render={({ field, fieldState }) => (
              <Box width="320px">
                <DateInput
                  errorText={fieldState.error?.message}
                  isPicker
                  isWide
                  testId="plan_end_date"
                  placeholder="Plan end date"
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  disabled
                  data-testid="plan-end-date-input"
                />
              </Box>
            )}
          />
        </ControllerWrapper>
      </FieldWrapper>
      <FieldWrapper style={{ justifyContent: 'space-between' }} data-testid="enrollment-status-wrapper">
        <Label
          showRequireIcon
          name={EnrollmentFormField.enrollmentStatus}
          isNarrow
          data-testid="enrollment-status-label"
        >
          Enrollment Status
        </Label>
        <ControllerWrapper style={{ flex: 'none' }} data-testid="enrollment-status-controller">
          <Controller
            name={EnrollmentFormField.enrollmentStatus}
            control={control}
            render={({ field, fieldState }) => (
              <SelectDropdown
                id="enrollmentStatus"
                name={EnrollmentFormField.enrollmentStatus}
                options={EnrollmentStatusOptions}
                singleMode
                values={field?.value ? [field.value] : ['']}
                placeholder="Enrollment status"
                disabled={!isEdit}
                onChange={field.onChange}
                errorText={fieldState.error?.message}
                inputStyle={{ minWidth: '320px', maxWidth: '320px' }}
                data-testid="enrollment-status-select"
              />
            )}
          />
        </ControllerWrapper>
      </FieldWrapper>
      <FieldWrapper style={{ justifyContent: 'space-between' }} data-testid="election-amount-wrapper">
        <Label
          showRequireIcon
          name={EnrollmentFormField.electionAmount}
          isNarrow
          data-testid="election-amount-label"
        >
          <Box>
            <Text margin={{ bottom: 'xsmall' }} weight="bold" data-testid="election-amount-title">
              Election amount
            </Text>
            <Box>
              <Text
                size="small"
                weight="normal"
                style={{ maxWidth: '220px' }}
                color="textSecondary"
                data-testid="election-amount-helper"
              >
                According to the chosen plan, the amount must be between {minAmount} and {maxAmount}.
              </Text>
            </Box>
          </Box>
        </Label>
        <ControllerWrapper
          style={{ flex: 'none', alignSelf: 'flex-start' }}
          data-testid="election-amount-controller"
        >
          <Controller
            name={EnrollmentFormField.electionAmount}
            control={control}
            render={({ field, fieldState }) => (
              <PriceControl
                {...field}
                placeholder="Election amount"
                errorText={fieldState.error?.message || ''}
                onChange={(value: string) => {
                  field.onChange(value);
                }}
                value={field.value ?? ''}
                data-testid="election-amount-input"
              />
            )}
          />
        </ControllerWrapper>
      </FieldWrapper>
      <FieldWrapper style={{ justifyContent: 'space-between' }} data-testid="per-pay-employer-wrapper">
        <Label name={EnrollmentFormField.perPayEmployer} isNarrow data-testid="per-pay-employer-label">
          Per Pay Employer
        </Label>
        <ControllerWrapper style={{ flex: 'none' }} data-testid="per-pay-employer-controller">
          <Controller
            name={EnrollmentFormField.perPayEmployer}
            control={control}
            render={({ field, fieldState }) => (
              <PriceControl
                {...field}
                placeholder="Per pay employer"
                errorText={fieldState.error?.message}
                onChange={(value: string) => field.onChange(value)}
                value={field.value ?? ''}
                data-testid="per-pay-employer-input"
              />
            )}
          />
        </ControllerWrapper>
      </FieldWrapper>
      <FieldWrapper style={{ justifyContent: 'space-between' }} data-testid="per-pay-employee-wrapper">
        <Label name={EnrollmentFormField.perPayEmployee} isNarrow data-testid="per-pay-employee-label">
          Per Pay Employee
        </Label>
        <ControllerWrapper style={{ flex: 'none' }} data-testid="per-pay-employee-controller">
          <Controller
            name={EnrollmentFormField.perPayEmployee}
            control={control}
            render={({ field, fieldState }) => (
              <PriceControl
                {...field}
                placeholder="Per pay employee"
                errorText={fieldState.error?.message || ''}
                onChange={(value: string) => field.onChange(value)}
                value={field.value ?? ''}
                data-testid="per-pay-employee-input"
              />
            )}
          />
        </ControllerWrapper>
      </FieldWrapper>
      {currentPlan?.luGpDesignationAllowed && (
        <FieldWrapper style={{ justifyContent: 'space-between' }} data-testid="limited-use-wrapper">
          <Label name={EnrollmentFormField.limitedUse} isNarrow data-testid="limited-use-label">
            Limited Use / General Purpose
          </Label>
          <ControllerWrapper style={{ flex: 'none' }} data-testid="limited-use-controller">
            <Controller
              name={EnrollmentFormField.limitedUse}
              control={control}
              render={({ field, fieldState }) => (
                <SelectDropdown
                  id="limitedUse"
                  name={EnrollmentFormField.limitedUse}
                  options={LimitedUseOptions}
                  singleMode
                  values={field?.value ? [field.value] : ['']}
                  placeholder="Limited Use"
                  onChange={field.onChange}
                  inputStyle={{ minWidth: '320px', maxWidth: '320px' }}
                  errorText={fieldState.error?.message}
                  data-testid="limited-use-select"
                />
              )}
            />
          </ControllerWrapper>
        </FieldWrapper>
      )}
      {currentPlan?.contributionMonitoringRequired && (
        <Box gap="spacing16" data-testid="coverage-level-section">
          <FieldWrapper style={{ justifyContent: 'space-between' }} data-testid="coverage-level-wrapper">
            <Label name={EnrollmentFormField.coverageLevel} isNarrow data-testid="coverage-level-label">
              Coverage Level
            </Label>
            <ControllerWrapper style={{ flex: 'none' }} data-testid="coverage-level-controller">
              <Controller
                name={EnrollmentFormField.coverageLevel}
                control={control}
                render={({ field, fieldState }) => (
                  <SelectDropdown
                    id="coverageLevel"
                    name={EnrollmentFormField.coverageLevel}
                    options={[
                      { key: '', value: '', title: 'None' },
                      {
                        key: COVERAGE_LEVEL_SINGLE_KEY,
                        value: COVERAGE_LEVEL_SINGLE_KEY,
                        title: COVERAGE_LEVEL_SINGLE_LABEL,
                      },
                      {
                        key: COVERAGE_LEVEL_FAMILY_KEY,
                        value: COVERAGE_LEVEL_FAMILY_KEY,
                        title: COVERAGE_LEVEL_FAMILY_LABEL,
                      },
                    ]}
                    singleMode
                    values={field?.value ? [field.value] : ['']}
                    placeholder="Coverage Level"
                    onChange={field.onChange}
                    inputStyle={{ minWidth: '320px', maxWidth: '320px' }}
                    errorText={fieldState.error?.message}
                    data-testid="coverage-level-select"
                  />
                )}
              />
            </ControllerWrapper>
          </FieldWrapper>
          <FieldWrapper
            style={{ justifyContent: 'space-between' }}
            data-testid="coverage-level-effective-date-wrapper"
          >
            <Label
              name={EnrollmentFormField.coverageLevelEffectiveDate}
              isNarrow
              showRequireIcon={!!coverageLevel}
              data-testid="coverage-level-effective-date-label"
            >
              Coverage Level Effective Date
            </Label>
            <ControllerWrapper
              style={{ flex: 'none' }}
              data-testid="coverage-level-effective-date-controller"
            >
              <Controller
                name={EnrollmentFormField.coverageLevelEffectiveDate}
                control={control}
                render={({ field, fieldState }) => (
                  <Box width="320px">
                    <DateInput
                      isPicker
                      isWide
                      errorText={fieldState.error?.message}
                      testId="coverage_level_effective_date"
                      placeholder="Change date"
                      value={field.value ?? ''}
                      onChange={field.onChange}
                      disabled={!coverageLevel}
                      data-testid="coverage-level-effective-date-input"
                    />
                  </Box>
                )}
              />
            </ControllerWrapper>
          </FieldWrapper>
        </Box>
      )}
      {limitedUse === LimitedUses.GENERALE_PURPOSE_DEDUCTIBLE_MET && (
        <FieldWrapper style={{ justifyContent: 'space-between' }} data-testid="deductible-met-date-wrapper">
          <Label
            showRequireIcon
            name={EnrollmentFormField.deductibleMetDate}
            isNarrow
            data-testid="deductible-met-date-label"
          >
            Deductible Met Date
          </Label>
          <ControllerWrapper style={{ flex: 'none' }} data-testid="deductible-met-date-controller">
            <Controller
              name={EnrollmentFormField.deductibleMetDate}
              control={control}
              render={({ field, fieldState }) => (
                <Box width="320px">
                  <DateInput
                    isPicker
                    isWide
                    testId="deductible_met_date"
                    placeholder="Change date"
                    value={field.value ?? ''}
                    onChange={field.onChange}
                    errorText={fieldState.error?.message}
                    data-testid="deductible-met-date-input"
                  />
                </Box>
              )}
            />
          </ControllerWrapper>
        </FieldWrapper>
      )}
      <FieldWrapper style={{ justifyContent: 'space-between' }} data-testid="enrollment-date-wrapper">
        <Label
          showRequireIcon
          name={EnrollmentFormField.enrollmentDate}
          isNarrow
          data-testid="enrollment-date-label"
        >
          <Box>
            <Text margin={{ bottom: 'xsmall' }} weight="bold" data-testid="enrollment-date-title">
              Change date
            </Text>
            <Box>
              <Text
                size="small"
                weight="normal"
                style={{ maxWidth: '220px' }}
                color="textSecondary"
                data-testid="enrollment-date-helper"
              >
                This is the date these changes become effective
              </Text>
            </Box>
          </Box>
        </Label>
        <ControllerWrapper
          style={{ flex: 'none', alignSelf: 'flex-start' }}
          data-testid="enrollment-date-controller"
        >
          <Controller
            name={EnrollmentFormField.enrollmentDate}
            control={control}
            render={({ field, fieldState }) => (
              <Box width="320px">
                <DateInput
                  isPicker
                  errorText={fieldState.error?.message}
                  isWide
                  testId="enrollment_date"
                  placeholder="Change date"
                  value={field.value ?? ''}
                  onChange={(value: string) => {
                    field.onChange(value);
                    onChangeDateChange?.();
                  }}
                  data-testid="enrollment-date-input"
                />
              </Box>
            )}
          />
        </ControllerWrapper>
      </FieldWrapper>
    </Box>
  );
};

export default React.memo(PlanFields);
